import axios from 'axios'
import { updateMfaGraceFromHeaders, clearMfaGrace } from './mfaGraceSignal'
import { describeApiError, retryAfterFrom } from './apiErrors'

const API_BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : 'http://localhost:3000/api/v1'

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
  withCredentials: true,
})

// Token management
// Tokens are now stored as httpOnly cookies, not in localStorage
// These functions are kept for compatibility but don't store tokens in localStorage

export const setTokens = (accessToken, refresh) => {
  // Tokens are handled by the backend via httpOnly cookies
  // No need to store them in memory or localStorage
  // CSRF token is handled separately as a non-httpOnly cookie
}

export const getAuthToken = () => {
  // Access token is in httpOnly cookie, not accessible from JS
  // Return undefined since we can't read it
  return undefined
}

export const getRefreshToken = () => {
  // Refresh token is in httpOnly cookie, not accessible from JS
  // Return undefined since we can't read it
  return undefined
}

export const clearTokens = () => {
  // Tokens are cleared via httpOnly cookies by the backend
  // Only clear localStorage items that we manage
  localStorage.removeItem('user')
  localStorage.removeItem('csrfToken')
  // Sign-out ends the grace window for this tab: clear required AND answered
  // (house-check #987 — resetting only `answered` left the sticky `required`
  // flag standing and rendered the prompt on /login). The next sign-in's
  // responses re-establish the window from the headers.
  clearMfaGrace()
}

// Request interceptor - add CSRF token and request ID
// Access token is now in httpOnly cookie and sent automatically
apiClient.interceptors.request.use(
  (config) => {
    // Access token is in httpOnly cookie and sent automatically by the browser
    // No need to add Authorization header here

    // Read CSRF token from cookie first, fall back to localStorage
    // (cookie may not be available cross-origin if onrender.com PSL blocks it)
    const csrfFromCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrf-token='))
      ?.split('=')[1]
    const csrfToken = csrfFromCookie || localStorage.getItem('csrfToken')
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken
    }

    // Add unique request ID for traceability
    config.headers['X-Request-ID'] = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - unwrap { success, data } envelope and handle 401 refresh
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })
  failedQueue = []
}

apiClient.interceptors.response.use(
  // Bug #36 fix (2026-05-22): the auto-unwrap was added in commit b987b31
  // ("Fix Governance, Users, and Roles page errors") to simplify a few
  // specific call sites but it silently broke 22+ pre-existing call sites
  // (TriageDashboard / ReviewQueue / InvestigationList / NewTriageCase /
  // NewInvestigation / TriageCaseDetail / InvestigationDetail / WhistleblowingConfig)
  // that all do `if (response.data.success) { const data = response.data.data }`.
  // With the auto-unwrap, `response.data.success` was always undefined (the
  // success field was stripped) so the conditional bodies never ran and
  // every list endpoint rendered as empty. Reverting to identity passthrough;
  // every call site is responsible for unwrapping its own envelope. Any code
  // added between b987b31 and this revert that depended on auto-unwrap is
  // tracked under Bug #36-followup — easier to fix the few new sites than to
  // leave the 22 old sites broken.
  (response) => {
    // Read the MFA grace-window headers the backend has been setting since the
    // control shipped (mfaEnforcement.js:173-175) and which, until now, nothing
    // read. Passthrough is preserved exactly — this observes, it does not alter
    // the response, so the Bug #36 revert above still holds.
    updateMfaGraceFromHeaders(response?.headers)
    return response
  },
  async (error) => {
    // Headers arrive on failures too, and a 4xx is exactly when an administrator
    // most needs to know the window is closing.
    updateMfaGraceFromHeaders(error?.response?.headers)
    const originalRequest = error.config

    // MFA enforcement has bitten (mfaEnforcement.js 403 MFA_ENROLMENT_REQUIRED). Until
    // 2 Oct 2026 this fell through to the generic handling: the shell's own calls failed,
    // the user was bounced to /login, and the sign-in itself was then refused. Send them
    // to the standalone enrolment route instead, once — never from that route itself.
    if (error.response?.status === 403 && error.response?.data?.error?.code === 'MFA_ENROLMENT_REQUIRED') {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/mfa/enrol')) {
        window.location.assign('/mfa/enrol')
      }
      return Promise.reject(error)
    }

    // Handle CSRF token mismatch - refresh token and retry
    if (error.response?.status === 403 && error.response?.data?.error?.code === 'CSRF_TOKEN_INVALID' && !originalRequest._csrfRetry) {
      originalRequest._csrfRetry = true
      try {
        // Fetch a fresh CSRF token by hitting the auth refresh or any GET endpoint
        const csrfResponse = await axios.get(`${API_BASE_URL}/auth/csrf-token`, { withCredentials: true })
        const newCsrfToken = csrfResponse.data?.csrfToken || csrfResponse.data?.data?.csrfToken
        if (newCsrfToken) {
          localStorage.setItem('csrfToken', newCsrfToken)
          originalRequest.headers['X-CSRF-Token'] = newCsrfToken
        }
        return apiClient(originalRequest)
      } catch (csrfErr) {
        // If CSRF refresh fails, continue to the error handler below
        console.warn('CSRF token refresh failed, continuing with original error')
      }
    }

    // A 401 from a PRE-AUTHENTICATION endpoint (login, logout, register,
    // invite acceptance, password reset, MFA verify) is a credential/input
    // failure — NOT an expired session. Running the token-refresh cycle here is
    // wrong and actively harmful: the refresh fails with "Refresh token not
    // found", and that message then masks the real reason (wrong password /
    // account not active / locked). Skip the refresh cycle for these so the
    // backend's actual error message reaches the caller. /auth/refresh is
    // deliberately NOT in this list, so a genuinely expired session still
    // refreshes-then-redirects as before.
    const reqUrl = originalRequest?.url || ''
    const isPreAuthEndpoint = /\/auth\/(login|logout|register|accept-invite|password\/reset|mfa\/verify)/.test(reqUrl)

    if (error.response?.status === 401 && !originalRequest._retry && !isPreAuthEndpoint) {
      if (isRefreshing) {
        // Queue this request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(() => {
          // Access token is in httpOnly cookie, just retry the request
          return apiClient(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Call refresh endpoint - it will set new accessToken and refreshToken cookies
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true })

        // Handle both wrapped { success, data: { ... } } and unwrapped formats
        const payload = response.data?.data || response.data

        // No need to store tokens in memory, they're in httpOnly cookies now
        processQueue(null, true)

        // Retry the original request (cookies will be sent automatically)
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        clearTokens()
        // Only redirect to /login if not already there to prevent redirect loops
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // 429: the limiter is identity-keyed and cheap to wait out (PR-A). Retry
    // ONCE after Retry-After, capped at 10 s so a page never hangs; a second
    // 429 falls through to describeApiError and the user is told.
    if (error.response?.status === 429 && !originalRequest._rateLimitRetry) {
      const secs = retryAfterFrom(error)
      if (secs && secs <= 10) {
        originalRequest._rateLimitRetry = true
        await new Promise((r) => setTimeout(r, secs * 1000))
        return apiClient(originalRequest)
      }
    }

    if (error.response?.data) {
      const message = error.response.data.message || error.response.data.error?.message || error.response.statusText
      error.message = message
    }

    // Normalise 429 / 5xx / 400-validation / network into error.userMessage,
    // error.kind, error.fieldErrors; 429, 5xx and network are also announced
    // to the toast bridge (see lib/apiErrors.js). Validation stays with the
    // form that made the request.
    describeApiError(error)

    return Promise.reject(error)
  }
)

export default apiClient
